import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// components
import { Modal } from '@interstate/components/Modal';
import { Button } from '@interstate/components/Button';
import { Interstitial as LoadingIndicator } from '@interstate/components/Interstitial';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as offerInfoSelectors from '@makemydeal/dr-dash-store/selectors/offerInfoSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { Typography } from '@interstate/components/Typography';
import type { UpdateOfferDialogProps } from './UpdateOfferDialogProps';
import { UpdateOfferDialogMode } from './UpdateOfferDialogMode';
import { ErrorModalDialogContent } from '../common/ErrorModalDialogContent';
import { LoaderWrapper, UpdateOfferErrorFooter } from './UpdateOfferDialog.style';
import { useValidationGuard } from '../hooks/useValidationGuard';

export const UpdateOfferDialog = ({ show, onHide, onSuccess, waitForWS, onDialogModeChange }: UpdateOfferDialogProps) => {
    const dispatch = useDispatch();
    const guard = useValidationGuard('save', { skipValidationKeys: ['vehicle'] });
    const [mode, setMode] = useState(UpdateOfferDialogMode.SAVING);
    const offerIsBeingSaved = useSelector(offerInfoSelectors.getIsOfferBeingSaved);
    const offerHasBeenSaved = useSelector(offerInfoSelectors.getHasBeenSaved);
    const searchAndMergeCleanedUp = useSelector(offerInfoSelectors.getSearchAndMergeCleanedUp);
    const isDealXgRecordMissed = useSelector(offerReduxSelectors.getIsDealXgRecordMissed);

    const setDialogMode = (mode: UpdateOfferDialogMode) => {
        if (onDialogModeChange) {
            onDialogModeChange(mode);
        }
        setMode(mode);
    };

    // Set mode when offer save status changes
    React.useEffect(() => {
        if (show) {
            if (offerIsBeingSaved === true) {
                return setDialogMode(UpdateOfferDialogMode.SAVING);
            }

            if (offerHasBeenSaved && !offerIsBeingSaved) {
                if (!waitForWS) {
                    setDialogMode(UpdateOfferDialogMode.SUCCESS);
                }
                if (onSuccess) {
                    onSuccess();
                }
                return;
            }

            if (offerIsBeingSaved === false && offerHasBeenSaved === false && !searchAndMergeCleanedUp) {
                setDialogMode(UpdateOfferDialogMode.ERROR);
                return;
            }

            // First two conditions aren't strictly necessary but leaving them in case these if statements
            // are reordered.
            if (offerIsBeingSaved === false && offerHasBeenSaved === false && searchAndMergeCleanedUp) {
                setDialogMode(UpdateOfferDialogMode.ERROR_409);
                return;
            }
        }
    }, [offerIsBeingSaved, offerHasBeenSaved, searchAndMergeCleanedUp, waitForWS]);
    const handleRetryClick = guard.wrap(() => dispatch(offerActionCreators.saveOffer()));
    const header = <Typography variant="h4">{isDealXgRecordMissed ? 'Save Offer' : 'Update Offer'}</Typography>;
    switch (mode) {
        case UpdateOfferDialogMode.SAVING:
            return (
                <Modal
                    size="small"
                    data-testid="update-offer-dialog-saving"
                    show={show}
                    header={header}
                    onHide={onHide}
                    sx={{
                        '#ids-modal-fixed-footer-component': {
                            padding: '20px 24px 24px'
                        }
                    }}
                    footer={{
                        footerComponent: (
                            <LoaderWrapper>
                                <LoadingIndicator size="small" data-testid="update-offer-loading-indicator" />
                            </LoaderWrapper>
                        )
                    }}
                >
                    <Typography variant="body-md" tag="p">
                        {isDealXgRecordMissed ? "We're saving your offer." : "We're updating your changes to this offer."}
                    </Typography>
                </Modal>
            );
        case UpdateOfferDialogMode.ERROR:
            return (
                <Modal
                    size="small"
                    data-testid="update-offer-dialog-error"
                    show={show}
                    header={header}
                    onHide={onHide}
                    footer={{
                        footerComponent: (
                            <UpdateOfferErrorFooter>
                                <Button
                                    data-testid="update-offer-retry-button"
                                    onClick={handleRetryClick}
                                    size="small"
                                    buttonStyle="primary"
                                >
                                    Retry
                                </Button>
                            </UpdateOfferErrorFooter>
                        )
                    }}
                >
                    <ErrorModalDialogContent
                        alertString={
                            isDealXgRecordMissed
                                ? ' Saving this offer failed. Please try again.'
                                : ' The changes you made to this offer failed to update. Please try again.'
                        }
                        contentString={
                            isDealXgRecordMissed
                                ? "We ran into a problem saving this offer so it wasn't created. Please try again."
                                : "We ran into a problem updating this offer so the information wasn't shared. Please try again."
                        }
                    />
                </Modal>
            );
        case UpdateOfferDialogMode.ERROR_409:
            return (
                <Modal
                    size="small"
                    data-testid="update-offer-dialog-error"
                    show={show}
                    header={header}
                    onHide={onHide}
                    sx={{
                        '#ids-modal-content': {
                            paddingBottom: '48px'
                        }
                    }}
                >
                    <ErrorModalDialogContent
                        alertString=" The changes you made to this offer failed to update."
                        contentString="This deal can no longer be updated because its credit application has been attached to another deal. Please continue working from the primary deal."
                        hideSupportInfo={searchAndMergeCleanedUp}
                    />
                </Modal>
            );
        default: {
            return null;
        }
    }
};
